import { useEffect, useRef, useCallback, use } from "react";
import { GameProps } from "shared/games/types/GameConfiguration";
import { GameAnswerIsThisCompressorOn } from "shared/games/types/GameAnswers";
import {
  GameQuestionIsThisCompressorOn,
  IsThisCompressorOnAnswerOptions,
} from "shared/games/types/GameQuestions";
import * as GameLayout from "ui/templates/Game/LayoutComponents";
import Button from "ui/atoms/Button/Button";
import Switch, { SwitchItem } from "ui/atoms/Switch/Switch";
import { useMountEffect, useUnmountEffect } from "@react-hookz/web";
import ABPicker from "ui/organisms/ABPicker/ABPicker";
import { InstrumentPlayer } from "shared/AudioPlayers/InstrumentPlayer/InstrumentPlayer";
import useAutoToggle from "ui/templates/Game/hooks/useAutoToggle";
import { referenceTrackSteps, refrenceTrackIds } from "./referenceTrackConfig";
import ReferenceTrack from "ui/organisms/ReferenceTrack/ReferenceTrack";

type Props = GameProps<
  GameQuestionIsThisCompressorOn,
  GameAnswerIsThisCompressorOn
>;

type PlaybackState = keyof GameQuestionIsThisCompressorOn["options"];

export default function IsThisCompOnGame({
  question,
  onLoaded,
  onSubmit,
  controlledPlayback,
  mobileToolbarStyle,
  referenceTrackDisabled,
}: Props) {
  const audioPlayer = useRef<InstrumentPlayer>();
  const {
    playbackState,
    setPlaybackState,
    autoToggle,
    setAutoToggle,
    secondsTillChange,
  } = useAutoToggle<PlaybackState>({
    defaultPlaybackState: "a",
    looop: question.looop,
    getNextPlaybackState: (prev) => (prev === "a" ? "b" : "a"),
    enabled: controlledPlayback !== "pause",
  });

  useMountEffect(() => {
    const player = new InstrumentPlayer(question.instrument, question.looop, [
      "default",
      "comp_opto1",
      "comp_opto2",
      "comp_opto4",
      "comp_opto7",
      "comp_opto10",
    ]);

    player.load().then(() => {
      if (controlledPlayback !== "pause") {
        player.start();
      }

      onLoaded();
    });

    audioPlayer.current = player;
  });

  useEffect(() => {
    if (controlledPlayback === "pause") {
      audioPlayer.current?.stop();
    } else {
      audioPlayer.current?.start();
    }
  }, [controlledPlayback]);

  useUnmountEffect(() => {
    audioPlayer.current?.dispose();
    audioPlayer.current = undefined;
  });

  useEffect(() => {
    if (!audioPlayer.current) {
      return;
    }

    if (question.options.a === question.options.b) {
      audioPlayer.current.sync({
        [question.options.a]: {
          audioFile: question.options.a,
          controls: { muted: false },
        },
      });
    } else {
      audioPlayer.current.sync({
        [question.options.a]: {
          audioFile: question.options.a,
          controls: {
            muted: playbackState === "b",
          },
        },
        [question.options.b]: {
          audioFile: question.options.b,
          controls: {
            muted: playbackState === "a",
          },
        },
      });
    }
  }, [playbackState, question.options.a, question.options.b]);

  const handleSubmit = useCallback(
    (value: IsThisCompressorOnAnswerOptions) => {
      const correct = value === question.answer;

      onSubmit({ type: question.type, correct, submission: value });
    },
    [onSubmit, question.answer, question.type]
  );

  return (
    <>
      <GameLayout.Toolbar mobileStyle={mobileToolbarStyle}>
        <div className={"flex justify-between"}>
          <div className={"flex items-center"}>
            <div className={"mr-2"} id={refrenceTrackIds.playbackState}>
              <Switch
                size="large"
                items={items}
                value={playbackState}
                wholeButtonToggle
                onChange={setPlaybackState}
              />
            </div>

            <Button
              data-sdf
              theme={"default"}
              size="large"
              outline
              active={autoToggle}
              onClick={() => setAutoToggle((x) => !x)}
            >
              Auto Toggle {secondsTillChange}
            </Button>
          </div>
        </div>
      </GameLayout.Toolbar>

      <GameLayout.Container>
        <div
          id={refrenceTrackIds.abPicker}
          className={
            "flex items-center justify-center overflow-hidden p-24 md:p-32"
          }
        >
          <ABPicker
            onClick={handleSubmit}
            questionText={"Is more compressed?"}
            playbackState={playbackState}
          />
        </div>
      </GameLayout.Container>

      <ReferenceTrack
        localStorageKey={"is-this-comp-on"}
        getSteps={referenceTrackSteps}
        disabled={referenceTrackDisabled}
      />
    </>
  );
}

const items: SwitchItem<PlaybackState>[] = [
  { label: "A", value: "a" },
  { label: "B", value: "b" },
];
